import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { axiosDoctorProfile } from "../../../../api/axios";
import { getDoctorSharedDocuments } from "../../../../medicalRecord/redux/thunks/sharingthunks";
import "./DocumentsTab.css";
import BulletinDeSoins from "../BulletinDeSoins/BulletinDeSoins";
import {
  useCnssCards,
  useMedicalActs,
} from "../../../../consultation/hooks/useCnss";

// ── Grouper les actes CNSS par date → un bulletin par jour ──────────────────
function groupActsIntoBulletins(acts) {
  const map = new Map();

  for (const act of acts) {
    const dayKey = act.createdAt
      ? String(act.createdAt).slice(0, 10)
      : "unknown";

    if (!map.has(dayKey)) {
      map.set(dayKey, {
        id: `bs-group-${dayKey}`,
        createdAt: act.createdAt,
        date: dayKey,
        acts: [],
      });
    }
    map.get(dayKey).acts.push(act);
  }

  return Array.from(map.values()).sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
}

export default function DocumentsTab({ userId, doctorId, sharedFiles = [] }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [files, setFiles] = useState([]);
  const [localSharedFiles, setLocalSharedFiles] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [previewFileBlob, setPreviewFileBlob] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [imageError, setImageError] = useState(false);
  const [openBulletin, setOpenBulletin] = useState(false);

  const [selectedBsActs, setSelectedBsActs] = useState(null);
  const [isCnamConventioned, setIsCnamConventioned] = useState(false);
  const [insuranceLoaded, setInsuranceLoaded] = useState(false);

  const { cards, fetchByPatient } = useCnssCards();
  const { medicalActs, fetchActs } = useMedicalActs();

  

  const previewFileName =
    previewDoc?.name || previewDoc?.title || previewDoc?.nom_fichier || "";

  const isImage =
    previewFileUrl &&
    /\.(jpg|jpeg|png|gif|webp|bmp|tiff)$/i.test(previewFileName);

  const isPDF = previewFileUrl && /\.pdf$/i.test(previewFileName);

  // ── Bulletins construits depuis la BD (plus de localStorage) ─────────────
  useEffect(() => {
    if (!doctorId) {
      setIsCnamConventioned(false);
      setInsuranceLoaded(true);
      return undefined;
    }

    let active = true;
    setInsuranceLoaded(false);
    axiosDoctorProfile
      .get(`/doctor-info/by-doctor/${encodeURIComponent(String(doctorId))}`)
      .then(({ data }) => {
        if (!active) return;
        const insurances = Array.isArray(data?.insurances) ? data.insurances : [];
        setIsCnamConventioned(
          insurances.some((insurance) => String(insurance || "").trim().toUpperCase() === "CNAM")
        );
      })
      .catch(() => active && setIsCnamConventioned(false))
      .finally(() => active && setInsuranceLoaded(true));

    return () => {
      active = false;
    };
  }, [doctorId]);
  const bulletins = useMemo(
    () => groupActsIntoBulletins(Array.isArray(medicalActs) ? medicalActs : []),
    [medicalActs]
  );

  // ── Chargement initial ────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId || !insuranceLoaded || !isCnamConventioned) return;

    fetchByPatient(userId);
    fetchActs(userId);

    // Re-fetch quand le BS vient d'être enregistré
    const onActsUpdated = (e) => {
      if (String(e?.detail?.patientId) === String(userId)) {
        fetchActs(userId);
      }
    };

    const onFocus = () => fetchActs(userId);

    window.addEventListener("cnss-medical-acts-updated", onActsUpdated);
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("cnss-medical-acts-updated", onActsUpdated);
      window.removeEventListener("focus", onFocus);
    };
  }, [userId, insuranceLoaded, isCnamConventioned, fetchByPatient, fetchActs]);

  // ── Documents partagés via API ────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    setApiLoading(true);

    dispatch(getDoctorSharedDocuments({ skip: 0, limit: 200 }))
      .unwrap()
      .then((result) => {
        const rawDocs = result?.documents || [];
        if (rawDocs.length === 0) return;

        const normalized = rawDocs.map((entry) => {
          const d = entry?.document ?? entry;
          const access = entry?.accessInfo ?? {};

          const patientId = d?.user_id ?? d?.patientId ?? d?.patient_id ?? null;
          const collection =
            d?._collection ?? d?.type_document?.toLowerCase() ?? null;
          const docId = d?._id ?? d?.id ?? null;
          const storagePath = d?.chemin_fichier || d?.file_path || null;
          const fallbackName = d?.id_doc || d?.nom_fichier || null;
          const fileUrl =
            storagePath ||
            d?.fileUrl ||
            d?.pdfUrl ||
            d?.url ||
            (collection && fallbackName
              ? `${collection}/${fallbackName}`
              : null);

          return {
            id: docId,
            _id: docId,
            title:
              d?.nom_fichier || d?.id_doc || d?.type_document || "Document",
            name:
              d?.nom_fichier || d?.id_doc || d?.type_document || "Document",
            type_document: d?.type_document,
            _collection: collection,
            id_doc: d?.id_doc ?? null,
            chemin_fichier: d?.chemin_fichier ?? null,
            file_path: d?.file_path ?? null,
            fileUrl,
            pdfUrl: fileUrl,
            href: fileUrl,
            patientId,
            sharedDoctors: [],
            hasExpiration: !access?.isPermanent && !!access?.expiresAt,
            expiresAt: access?.expiresAt ?? null,
            sharedAt: access?.sharedAt ?? null,
            _raw: d,
            _access: access,
          };
        });

        if (normalized.length === 0) return;

        setLocalSharedFiles((prev) => {
          const map = new Map(
            [...prev, ...normalized].map((d) => [
              String(d.id || d._id || d.fileUrl || d.title || Math.random()),
              d,
            ])
          );
          return Array.from(map.values());
        });
      })
      .catch(() => {})
      .finally(() => setApiLoading(false));
  }, [dispatch, userId]);

  // ── Ecouter l'événement de mise à jour des docs partagés (API uniquement) ─
  useEffect(() => {
    if (!userId) return;

    const onSharedDocsUpdated = (e) => {
      if (Array.isArray(e?.detail?.docs) && e.detail.docs.length > 0) {
        setLocalSharedFiles(e.detail.docs);
      }
    };

    window.addEventListener("shared-medical-docs-updated", onSharedDocsUpdated);

    return () => {
      window.removeEventListener(
        "shared-medical-docs-updated",
        onSharedDocsUpdated
      );
    };
  }, [userId]);

  const cnssCard = useMemo(() => {
    return Array.isArray(cards) && cards.length > 0 ? cards[0] : null;
  }, [cards]);

  const canShowCnamBook = isCnamConventioned && Boolean(cnssCard);

  const upload = (e) => {
    setFiles([...files, ...Array.from(e.target.files)]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const openDocumentPreview = useCallback((doc, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    setPreviewDoc(doc);
    setImageError(false);
  }, []);

  const closeDocumentPreview = useCallback(() => {
    setPreviewDoc(null);
    setImageError(false);
  }, []);

  useEffect(() => {
    if (previewFileUrl) URL.revokeObjectURL(previewFileUrl);

    setPreviewFileUrl(null);
    setPreviewFileBlob(null);
    setPreviewError("");
    setImageError(false);

    if (!previewDoc) return;

    if (previewDoc.file && previewDoc.type === "uploaded") {
      const objectUrl = URL.createObjectURL(previewDoc.file);
      setPreviewFileUrl(objectUrl);
      setPreviewFileBlob(previewDoc.file);
      return;
    }

    const originalPath =
      previewDoc.chemin_fichier ||
      previewDoc.file_path ||
      previewDoc.fileUrl;

    if (!originalPath) return;

    let cleanPath = String(originalPath)
      .replace(/\\\\/g, "/")
      .replace(/\\/g, "/")
      .replace(/\/\//g, "/");

    cleanPath = cleanPath.replace(/^\/app\/storage\//, "");

    const parts = cleanPath
      .replace(/^storage\//, "")
      .split("/")
      .filter(Boolean);

    if (parts.length < 2) return;

    const collection = parts[0];
    const filename = parts.slice(1).join("/");

    let backendUrl = import.meta.env.VITE_GATEWAY_URL;

    if (!backendUrl) {
      backendUrl = "https://back.mediwave.tn/api";
    }

    const url = `${backendUrl}/extraction/consultation/${collection}/${encodeURIComponent(
      filename
    )}`;

    const fetchFile = async () => {
      try {
        const jwtToken = Cookies.get("accessToken");
        if (!jwtToken) throw new Error("JWT token not found");

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${jwtToken}` },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }

        const blob = await response.blob();
        setPreviewFileBlob(blob);
        setPreviewFileUrl(URL.createObjectURL(blob));
        setImageError(false);
      } catch (err) {
        console.error("[DocumentsTab] ❌ Error fetching file:", err);
        setPreviewFileUrl(null);
        setPreviewFileBlob(null);
        setImageError(true);
      }
    };

    fetchFile();
  }, [previewDoc]);

  const allDocuments = useMemo(() => {
    const uploadedDocs = files.map((f, idx) => ({
      id: `upload-${Date.now()}-${idx}-${f.name}`,
      type: "uploaded",
      file: f,
      name: f.name,
      title: f.name,
    }));

    const sharedMap = new Map();

    [
      ...(Array.isArray(sharedFiles) ? sharedFiles : []),
      ...(Array.isArray(localSharedFiles) ? localSharedFiles : []),
    ].forEach((doc) => {
      const key = String(
        doc.id || doc._id || doc.fileUrl || doc.pdfUrl || doc.title || ""
      );
      if (key && !sharedMap.has(key)) {
        sharedMap.set(key, doc);
      }
    });

    const sharedDocs = Array.from(sharedMap.values()).map((doc) => ({
      id: doc.id || doc._id || `doc-${Math.random()}`,
      title: doc.title || doc.nom_fichier || doc.name || "Document",
      name: doc.title || doc.nom_fichier || doc.name || "Document",
      type: "shared",
      fileUrl: doc.fileUrl || doc.pdfUrl || doc.href || null,
      sharedDoctors: doc.sharedDoctors || [],
      hasExpiration: doc.hasExpiration || false,
      expiresAt: doc.expiresAt || null,
      ...doc,
    }));

    // Un item par bulletin (groupé par jour) dans la liste principale
    const bsDocs = canShowCnamBook ? bulletins.map((bulletin) => ({
      id: bulletin.id,
      title: `${t("consultationPage.documents.cnssBulletin")} — ${new Date(bulletin.date).toLocaleDateString()}`,
      name: `${t("consultationPage.documents.cnssBulletin")} — ${new Date(bulletin.date).toLocaleDateString()}`,
      type: "bs",
      patientId: userId,
      bulletin,
    })) : [];

    return [...bsDocs, ...uploadedDocs, ...sharedDocs];
  }, [files, sharedFiles, localSharedFiles, bulletins, userId, canShowCnamBook]);

  return (
    <div className="documents-tab">
      <div className="documents-header">
        <h3 className="documents-title">{t("consultationPage.tabs.documents")}</h3>

        <label className="documents-upload-btn">
          {t("consultationPage.documents.upload")}
          <input type="file" multiple onChange={upload} hidden />
        </label>
      </div>

      {canShowCnamBook && (
        <div className="documents-collapse-card">
        <button
          type="button"
          className="documents-collapse-head"
          // onClick={() => setOpenBulletin(!openBulletin)}
          onClick={() => {
            setSelectedBsActs(null);
            setOpenBulletin(!openBulletin);
          }}
        >
          <span className={`documents-arrow ${openBulletin ? "open" : ""}`}>
            ›
          </span>
          <span className="documents-collapse-title">
            {t("consultationPage.documents.cnssBulletin")}
          </span>
          <span className="documents-collapse-action">
            {openBulletin ? "×" : "+"}
          </span>
        </button>

        {openBulletin && (
          <div className="documents-collapse-content">
            {/* <BulletinDeSoins card={cnssCard} patientId={userId} /> */}
            <BulletinDeSoins
              card={cnssCard}
              patientId={userId}
              viewedActs={selectedBsActs}
            />
          </div>
        )}
      </div>
      )}

      {apiLoading && allDocuments.length === 0 ? (
        <div className="documents-empty">{t("consultationPage.documents.loading")}</div>
      ) : allDocuments.length === 0 ? (
        <p className="documents-empty-text">{t("consultationPage.documents.empty")}</p>
      ) : (
        <div className="documents-list">
          {allDocuments.map((doc, i) => (
            <div key={doc.id} className="documents-item">
              <div className="documents-info">
                <span className="documents-name">{doc.name}</span>

                {doc.type === "bs" && (
                  <div className="documents-shared-info">
                    <span className="documents-shared-badge">
                      {t("consultationPage.documents.actCount", { count: doc.bulletin?.acts?.length ?? 0 })}
                    </span>
                  </div>
                )}

                {doc.type === "shared" && doc.sharedDoctors?.length > 0 && (
                  <div className="documents-shared-info">
                    <span className="documents-shared-badge">
                      {t("consultationPage.documents.sharedWithDoctors", { count: doc.sharedDoctors.length })}
                    </span>

                    {doc.hasExpiration && doc.expiresAt && (
                      <span className="documents-expiration">
                        {t("consultationPage.documents.expires")}: {" "}
                        {new Date(doc.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="documents-item-actions">
                {(doc.fileUrl || doc.file || doc.type === "bs") && (
                  <button
                    type="button"
                    onClick={(e) => {
                      if (doc.type === "bs") {
                        e.preventDefault();
                        e.stopPropagation();
                     setSelectedBsActs(doc.bulletin?.acts || []);
                    setOpenBulletin(true);
                    return;

                      }
                      openDocumentPreview(doc, e);
                    }}
                    className="documents-link"
                  >
                    {t("consultationPage.documents.view")}
                  </button>
                )}

                {doc.type === "uploaded" && (
                  <button
                    type="button"
                    className="documents-remove"
                    onClick={() => removeFile(i)}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {previewDoc && (
        <div className="dt-docModalOverlay" onClick={closeDocumentPreview}>
          <div className="dt-docModal" onClick={(e) => e.stopPropagation()}>
            <div className="dt-docModalHead">
              <div className="dt-docModalHeadLeft">
                <span className="dt-docModalFileIcon" aria-hidden="true">
                  {isPDF ? "PDF" : t("consultationPage.documents.file")}
                </span>
                <p className="dt-docModalTitle">
                  {previewDoc.title || "Document"}
                </p>
              </div>

              <div className="dt-docModalHeadActions">
                {previewFileUrl && (
                  <a
                    href={previewFileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="dt-docModalOpenBtn"
                    aria-label={t("consultationPage.documents.openInNewTab")}
                    title={t("consultationPage.documents.openInNewTab")}
                  >
                    ↗
                  </a>
                )}

                <button
                  type="button"
                  className="dt-docModalClose"
                  onClick={closeDocumentPreview}
                  aria-label={t("common.close")}
                  title={t("common.close")}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="dt-docModalBody">
              {!previewFileUrl ? (
                <p className="dt-muted">
                  {previewError || t("consultationPage.documents.noPreviewFile")}
                </p>
              ) : isImage ? (
                <div className="dt-docImageContainer">
                  {imageError ? (
                    <div className="dt-docImageError">
                      <p className="dt-muted">
                        {t("consultationPage.documents.imageLoadFailed")}
                      </p>
                      <a
                        href={previewFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: "#2f7fb0",
                          marginTop: "12px",
                          display: "inline-block",
                        }}
                      >
                        {t("consultationPage.documents.openInNewTab")}
                      </a>
                    </div>
                  ) : (
                    <img
                      src={previewFileUrl}
                      alt={previewDoc?.title || t("consultationPage.documents.imageAlt")}
                      className="dt-docModalImage"
                      onError={() => setImageError(true)}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        cursor: "pointer",
                      }}
                      onClick={() => window.open(previewFileUrl, "_blank")}
                    />
                  )}
                </div>
              ) : (
                <iframe
                  title="document-preview"
                  src={previewFileUrl}
                  className="dt-docModalFrame"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

DocumentsTab.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  doctorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sharedFiles: PropTypes.arrayOf(PropTypes.object),
};